import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { WallNotification } from '../hooks/useNotifications';
import { useMarkNotificationRead } from '../hooks/useMarkNotificationRead';

interface NotificationItemProps {
  notification: WallNotification;
}

function getNotificationText(notification: WallNotification): { text: string; emoji?: string } {
  const actorName = notification.actor
    ? notification.actor.first_name && notification.actor.last_name
      ? `${notification.actor.first_name} ${notification.actor.last_name}`
      : notification.actor.username || 'Someone'
    : 'Someone';

  switch (notification.type) {
    case 'reaction':
      return { text: `${actorName} reacted to your post`, emoji: '🔥' };
    case 'comment':
      return { text: `${actorName} commented on your post`, emoji: '💬' };
    case 'mention':
      return { text: `${actorName} mentioned you in a post`, emoji: '@' };
    case 'follow':
      return { text: `${actorName} started following you`, emoji: '👤' };
    case 'badge':
      return { text: `You earned a new badge!`, emoji: '🏆' };
    case 'team_goal':
      return { text: `Your team reached a goal!`, emoji: '🎯' };
    case 'like':
      return { text: `${actorName} liked your post`, emoji: '❤️' };
    default:
      return { text: `${actorName} interacted with your content`, emoji: '📬' };
  }
}

function getNotificationLink(notification: WallNotification): string {
  switch (notification.type) {
    case 'follow':
      return `/profile/${notification.actor?.username || notification.actor_id}`;
    case 'comment':
    case 'mention':
    case 'reaction':
    case 'like':
      return `/post/${notification.post_id}`;
    default:
      return `/post/${notification.post_id}`;
  }
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const { markAsRead } = useMarkNotificationRead();
  const { text, emoji } = getNotificationText(notification);
  const link = getNotificationLink(notification);
  const relativeTime = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  const actorInitials = notification.actor
    ? (notification.actor.first_name?.[0] || '') + (notification.actor.last_name?.[0] || '')
    : '?';

  const handleClick = () => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  return (
    <Link
      to={link}
      onClick={handleClick}
      className={cn(
        "flex items-start gap-3 p-4 border-b border-border transition-colors",
        notification.is_read
          ? "bg-background hover:bg-muted/50"
          : "bg-primary/5 hover:bg-primary/10"
      )}
    >
      {/* Unread Indicator */}
      <div className="flex-shrink-0 w-2 pt-3">
        {!notification.is_read && (
          <div className="w-2 h-2 rounded-full bg-primary" />
        )}
      </div>

      {/* Actor Avatar */}
      <div className="relative flex-shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={notification.actor?.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm">
            {actorInitials || '?'}
          </AvatarFallback>
        </Avatar>
        {/* Emoji Badge */}
        <span className="absolute -bottom-1 -right-1 text-sm">
          {emoji}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm leading-snug",
          notification.is_read ? "text-foreground" : "text-foreground font-medium"
        )}>
          {text}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {relativeTime}
        </p>
      </div>
    </Link>
  );
}
